<script setup>
import { ref, onMounted, computed } from 'vue';
import { resolveAceleresApiBase } from 'shared/helpers/aceleresApiBase';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'vuex';
import axios from 'axios';
import SectionLayout from './SectionLayout.vue';

// Config (nível GESTOR) da transição automática de leads da API Oficial antes
// do fim da janela de 24h.
const store = useStore();
const { currentAccount } = useAccount();
const currentUser = computed(() => store.getters['auth/getCurrentUser']);

const LARAVEL_URL = `${resolveAceleresApiBase()}/api/aurora/handoff-config`;

const loaded = ref(false);
const loading = ref(true);
const saving = ref(false);
const savedMsg = ref('');
const errorMsg = ref('');
const loadErrorMsg = ref('');

const STAGE_OPTIONS = [
  {
    value: 'official_closing',
    label: 'Fechando janela Whats API',
  },
  {
    value: 'continuous_opening',
    label: 'Abrindo conversa em outro número',
  },
];

const MESSAGE_FIELDS = [
  {
    key: 'msg1',
    enabledKey: 'msg1_enabled',
    stageKey: 'msg1_stage',
    title: 'Mensagem 1',
    description: 'Texto padrão enviado antes de fechar a conversa oficial.',
  },
  {
    key: 'msg2',
    enabledKey: 'msg2_enabled',
    stageKey: 'msg2_stage',
    title: 'Mensagem 2',
    description: 'Texto padrão com número ou link do atendimento contínuo.',
  },
  {
    key: 'msg3',
    enabledKey: 'msg3_enabled',
    stageKey: 'msg3_stage',
    title: 'Mensagem 3',
    description:
      'Texto padrão de abertura pelo número de atendimento contínuo.',
  },
];

const defaultForm = () => ({
  enabled: true,
  buffer_min: 60,
  deadline_time: '',
  msg1_enabled: true,
  msg2_enabled: true,
  msg3_enabled: true,
  msg1_stage: 'official_closing',
  msg2_stage: 'official_closing',
  msg3_stage: 'continuous_opening',
  msg1: '',
  msg2: '',
  msg3: '',
});

const form = ref(defaultForm());

function validStage(stage) {
  return STAGE_OPTIONS.some(option => option.value === stage);
}

function headers() {
  return {
    'X-Chatwoot-Token': currentUser.value?.access_token || '',
    'X-Chatwoot-Account-Id': currentAccount.value?.id || '',
  };
}

async function carregar() {
  loading.value = true;
  loadErrorMsg.value = '';
  try {
    const resp = await axios.get(LARAVEL_URL, { headers: headers() });
    const c = resp.data.config || {};
    const defaults = defaultForm();
    form.value = {
      ...defaults,
      enabled: c.enabled !== false,
      buffer_min: Number(c.buffer_min ?? defaults.buffer_min),
      deadline_time: c.deadline_time || '',
      msg1_enabled: c.msg1_enabled !== false,
      msg2_enabled: c.msg2_enabled !== false,
      msg3_enabled: c.msg3_enabled !== false,
      msg1_stage: validStage(c.msg1_stage) ? c.msg1_stage : defaults.msg1_stage,
      msg2_stage: validStage(c.msg2_stage) ? c.msg2_stage : defaults.msg2_stage,
      msg3_stage: validStage(c.msg3_stage) ? c.msg3_stage : defaults.msg3_stage,
      msg1: c.msg1 || '',
      msg2: c.msg2 || '',
      msg3: c.msg3 || '',
    };
    loaded.value = true;
  } catch (error) {
    loaded.value = false;
    loadErrorMsg.value =
      error?.response?.status === 403
        ? 'Seu usuário não tem permissão para carregar essa configuração.'
        : 'Não foi possível carregar a configuração. Verifique a API da Acelerity e tente novamente.';
  } finally {
    loading.value = false;
  }
}

async function salvar() {
  saving.value = true;
  savedMsg.value = '';
  errorMsg.value = '';
  try {
    await axios.patch(
      LARAVEL_URL,
      { ...form.value, deadline_time: form.value.deadline_time || null },
      { headers: headers() }
    );
    savedMsg.value = 'Salvo!';
    setTimeout(() => {
      savedMsg.value = '';
    }, 3000);
  } catch {
    errorMsg.value =
      'Erro ao salvar. Verifique se você tem permissão de administrador.';
  } finally {
    saving.value = false;
  }
}

onMounted(carregar);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="w-full">
    <SectionLayout
      title="Mensagens automáticas da API Oficial"
      description="Configure as mensagens automáticas usadas para migrar leads da API Oficial para o número de atendimento contínuo antes do fechamento da janela de 24h."
      with-border
    >
      <div
        v-if="loading"
        class="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600"
      >
        Carregando configuração...
      </div>

      <div
        v-else-if="!loaded"
        class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
      >
        <p class="font-medium">Configuração não carregada</p>
        <p class="mt-1">{{ loadErrorMsg }}</p>
        <button
          type="button"
          class="mt-3 rounded-md border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-100"
          @click="carregar"
        >
          Tentar novamente
        </button>
      </div>

      <div v-else class="flex flex-col gap-5 mt-2">
        <label
          class="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-800"
        >
          <input v-model="form.enabled" type="checkbox" class="h-4 w-4" />
          <span>Transição automática ativada</span>
        </label>

        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label class="flex flex-col gap-1 text-sm text-slate-600">
            <span class="font-medium text-slate-700">
              Antecedência do disparo (min)
            </span>
            <input
              v-model.number="form.buffer_min"
              type="number"
              min="0"
              max="240"
              class="rounded-md border border-slate-200 px-3 py-2 text-sm"
            />
            <span class="text-xs text-slate-500">
              Quantos minutos antes do limite o sistema pode iniciar a
              transição.
            </span>
          </label>

          <label class="flex flex-col gap-1 text-sm text-slate-600">
            <span class="font-medium text-slate-700">
              Horário-limite da empresa
            </span>
            <input
              v-model="form.deadline_time"
              type="time"
              class="rounded-md border border-slate-200 px-3 py-2 text-sm"
            />
            <span class="text-xs text-slate-500">
              Em branco mantém o fim do expediente do vendedor.
            </span>
          </label>
        </div>

        <p class="text-xs text-slate-500">
          Variáveis disponíveis: <code>{nome}</code> (cliente),
          <code>{numero}</code> e <code>{link}</code> (WhatsApp do vendedor),
          <code>{vendedor}</code>.
        </p>

        <div class="flex flex-col gap-4">
          <div
            v-for="message in MESSAGE_FIELDS"
            :key="message.key"
            class="rounded-lg border border-slate-200 bg-white p-4"
            :class="{ 'opacity-60': !form[message.enabledKey] }"
          >
            <div
              class="mb-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
            >
              <div>
                <label
                  class="flex items-center gap-2 cursor-pointer text-sm font-semibold text-slate-800"
                >
                  <input
                    v-model="form[message.enabledKey]"
                    type="checkbox"
                    class="h-4 w-4"
                  />
                  <span>{{ message.title }}</span>
                </label>
                <p class="mt-1 text-xs text-slate-500">
                  {{ message.description }}
                </p>
              </div>

              <label
                class="flex min-w-[240px] flex-col gap-1 text-sm text-slate-600"
              >
                <span class="font-medium text-slate-700">Etapa de disparo</span>
                <select
                  v-model="form[message.stageKey]"
                  class="rounded-md border border-slate-200 px-3 py-2 text-sm"
                >
                  <option
                    v-for="option in STAGE_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
            </div>

            <textarea
              v-model="form[message.key]"
              rows="3"
              class="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              :disabled="!form[message.enabledKey]"
            />
          </div>
        </div>

        <div class="flex items-center gap-3 mt-1">
          <button
            type="button"
            :disabled="saving"
            class="rounded-md bg-woot-500 px-4 py-2 text-sm font-medium text-white hover:bg-woot-600 disabled:opacity-50 cursor-pointer"
            @click="salvar"
          >
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </button>
          <span v-if="savedMsg" class="text-sm text-green-600 font-medium">{{
            savedMsg
          }}</span>
          <span v-if="errorMsg" class="text-xs text-red-600">{{
            errorMsg
          }}</span>
        </div>
      </div>
    </SectionLayout>
  </div>
</template>
